import React, { useState } from 'react';
import styled from 'styled-components/macro';
import Button from './Button';
import { CommandBar, ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { forMinWidth } from '../../theme/breakpoints';
import { FeatureName, sendFeatureFeedback, FeatureSentiment } from '../../store/actions';
import { Fetchable } from '../../store/appState';
import { RootState } from '../../store/rootState';
import { useSelector } from 'react-redux';

export interface WasThisHelpfulProps {
    feature: FeatureName;

    /** Optional. When getting feedback for AI summary, this will contain the ID. */
    aiSummaryId?: number;
}

export default function WasThisHelpful(props: WasThisHelpfulProps): JSX.Element {
    const [commentOpen, setCommentOpen] = useState(false);
    const [commentText, setCommentText] = useState("")
    const feedbackState: Fetchable<string> | null = useSelector((state: RootState) => state.appState.featureFeedback);
    const [likeIcon, setLikeIcon] = useState("Like");
    const [dislikeIcon, setDislikeIcon] = useState("Dislike");

    let isLike: boolean = false;

    const _items: ICommandBarItemProps[] = [
        {
            key: 'yes',
            text: 'Yes',
            iconProps: { iconName: likeIcon },
            onClick: () => {
                isLike = true;
                setLikeIcon("LikeSolid");
                setDislikeIcon("Dislike");
                setCommentOpen(true);
                sendFeatureFeedback(props.feature, FeatureSentiment.Like, null, props.aiSummaryId);
            },
            canCheck: true,
        },
        {
            key: 'no',
            text: 'No',
            iconProps: { iconName: dislikeIcon },
            onClick: () => {
                isLike = false;
                setLikeIcon("Like");
                setDislikeIcon("DislikeSolid");
                setCommentOpen(true);
                sendFeatureFeedback(props.feature, FeatureSentiment.Dislike, null, props.aiSummaryId);
            },
        },
    ];

    function cancelClick(): void {
        setCommentOpen(false);
    }

    function sendClick(): void {
        setCommentOpen(false);
        // if no comment, don't send feedback and just close UI
        if (commentText && commentText.length > 0) {
            sendFeatureFeedback(props.feature,
                isLike ? FeatureSentiment.CommentForLike : FeatureSentiment.CommentForDislike,
                commentText,
                props.aiSummaryId);
        }
    }

    return (<Wrapper>
        <Heading>
            <div style={{ fontSize: "1.4rem", padding: "0 0.5rem" }}>Was this helpful?</div>
            <CommandBar className='bar' items={_items} styles={{ root: { padding: "0" } }}></CommandBar>
        </Heading>
        {feedbackState && <FeedbackSection>
            {feedbackState.isFetching && <div>Sending feedback...</div>}
            {feedbackState.errors && <div>Error: {feedbackState.errors[0]}</div>}
            {!feedbackState.isFetching && !feedbackState.errors && <div className="thanks">Thank you! We received your feedback.</div>}
        </FeedbackSection>}
        {commentOpen && <FeedbackSection>
            <label htmlFor="feature-comment"><span>Can you tell us more about your rating? (Optional)</span></label>
            <textarea name="feature-comment"
                id="feature-comment"
                rows={4}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
            />
            <div className="actions">
                <Button className='actionButton' buttonType='link' size='semiCompact' onClick={cancelClick}>Cancel</Button>
                <Button className='actionButton' buttonType='normal' size='semiCompact' onClick={sendClick}>Send</Button>
            </div>
        </FeedbackSection>}
    </Wrapper>)
}

const FeedbackSection = styled.div`
    font-size: 1.4rem;
    padding: 0.5rem;
    // border: 0.1rem solid gray;
    // border-radius: 0.4rem;

    textarea {
        width: 100%;
        margin-top: 0.5rem;
        margin-bottom: 0.5rem;
    }
    
    .actions {
        display: flex;
        align-items: right;
        justify-content: flex-end;
    }

    .actionButton {
        font-size: 1em;
    }

    .thanks {
        margin-bottom: 1rem;
        font-size: 1.3rem;
    }
`;
const Wrapper = styled.div`
    position: relative;
    margin: 1rem 0;

    .pin-icon {
        display: block;
        margin: 0 0 1rem 0;
        font-size: 1.5rem;
    }

    .pin-body {
        display: block;
        padding: 2rem 1rem 1rem 1rem;

        ${forMinWidth.md`
            padding: 2rem 2rem 1rem 1rem;
        `}
    }
    .toolbar {
        align-self: stretch;
        min-heidht: 2rem;
    }
    .bar {
        flex-grow: 1
    }
`;

const Heading = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
`;
const Description = styled.div`
    border: 0.1rem solid gray;
    border-radius: 0.5rem;
    padding: 1rem;
    word-break: break-word;

    pre, code {
        overflow-wrap: break-all;
        white-space: pre-wrap;
    }
`;

